import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * The hero's gradient rule is the progress bar (STYLE §6c): it flows while an operation runs
 * (save, submit, sync, filter fetch). Any code can turn it on through `useBusy()`.
 */
export interface BusyApi {
  /** True while anything is in progress. */
  busy: boolean;
  /** Marks an operation as started; call the returned function when it ends. */
  start: () => () => void;
  /** Keeps the bar flowing until the promise settles, and returns it. */
  track: <T>(work: Promise<T>) => Promise<T>;
}

const idle: BusyApi = {
  busy: false,
  start: () => () => undefined,
  track: (work) => work,
};

const BusyContext = createContext<BusyApi>(idle);

/**
 * Provides the busy state. It is on while any TanStack Query mutation runs (opt out with
 * `meta: { busy: false }`), while a query that opts in with `meta: { busy: true }` fetches,
 * and while any `start()` / `track()` call is open. Must sit inside a QueryClientProvider.
 */
export function BusyProvider({ children }: { children: ReactNode }): ReactElement {
  const [manual, setManual] = useState(0);
  const mutating = useIsMutating({ predicate: (mutation) => mutation.meta?.['busy'] !== false });
  const fetching = useIsFetching({ predicate: (query) => query.meta?.['busy'] === true });

  const start = useCallback((): (() => void) => {
    setManual((count) => count + 1);
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      setManual((count) => count - 1);
    };
  }, []);

  const track = useCallback(
    <T,>(work: Promise<T>): Promise<T> => {
      const end = start();
      return work.finally(end);
    },
    [start],
  );

  const value = useMemo<BusyApi>(
    () => ({ busy: manual > 0 || mutating > 0 || fetching > 0, start, track }),
    [manual, mutating, fetching, start, track],
  );

  return <BusyContext.Provider value={value}>{children}</BusyContext.Provider>;
}

/** The busy state and the functions that turn the hero progress bar on. */
export function useBusy(): BusyApi {
  return useContext(BusyContext);
}
